import { Component } from '@angular/core';
import { HyginestaftImageDialogComponent } from '../hyginestaft-image-dialog/hyginestaft-image-dialog.component';
import { HygieneStaffReport } from '../hygiene-staft-report.service';
import { HygieneBuildingLocation, HygineLiveService } from '../hygine-live.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { forkJoin } from 'rxjs';

type DateTab = 'today' | 'yesterday' | 'last7' | 'month';
type SlideType = 'combo' | 'map';

interface LiveSlide {
  type: SlideType;
  college?: 'COLLEGE' | 'SCHOOL';
  boarding?: 'RESIDENTIAL' | 'DAY_SCHOLAR';
  label: string;
  report: HygieneStaffReport | null;
  resolvedDateTab?: DateTab;
  mapReady?: boolean;
}

interface HygieneMapMarker {
  position: { lat: number; lng: number };
  title: string;
  options: google.maps.MarkerOptions;
}

@Component({
  selector: 'app-hygine-live-update',
  templateUrl: './hygine-live-update.component.html',
  styleUrls: ['./hygine-live-update.component.scss']
})
export class HygineLiveUpdateComponent {
   mapIconPath = 'assets/image/college-map-icon.svg';

  activeDateTab: DateTab = 'today';
  pageSize = 12;
  imageErrorIds = new Set<number>();
  private s3Base = (environment as any).s3imageUrl || '';
  private userPickedDateTab = false;

  /** Left-to-right priority: Today → Yesterday → Last 7 days → This Month */
  private readonly dateTabPriority: ReadonlyArray<{ tab: DateTab; filterType: string }> = [
    { tab: 'today', filterType: 'TODAY' },
    { tab: 'yesterday', filterType: 'YESTERDAY' },
    { tab: 'last7', filterType: 'LAST7DAYS' },
    { tab: 'month', filterType: 'THISMONTH' }
  ];

  /** Combination slides + Google Maps as the last slide */
  slides: LiveSlide[] = [
    { type: 'combo', college: 'COLLEGE', boarding: 'RESIDENTIAL', label: 'College · Residential', report: null },
    { type: 'combo', college: 'COLLEGE', boarding: 'DAY_SCHOLAR', label: 'College · Day scholar', report: null },
    { type: 'combo', college: 'SCHOOL', boarding: 'RESIDENTIAL', label: 'School · Residential', report: null },
    { type: 'combo', college: 'SCHOOL', boarding: 'DAY_SCHOLAR', label: 'School · Day scholar', report: null },
    { type: 'map', label: 'Google Maps', report: null }
  ];
  activeSlide = 0;
  private preloadingSlideIndex: number | null = null;

  mapCenter: { lat: number; lng: number } = { lat: 17.385044, lng: 78.486671 };
  mapZoom = 6;
  mapMarkers: HygieneMapMarker[] = [];
  private readonly defaultMapZoom = 6;

  private readonly autoRotateMs = 20000;
  private autoRotateTimer: ReturnType<typeof setInterval> | null = null;

  /** Silently re-fetch the API so values stay live */
  private readonly refreshMs = 15000;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private hygineLiveService: HygineLiveService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.fetchSlide(0, true, () => this.preloadNextSlide(), true);
    this.startAutoRotate();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRotate();
    this.stopAutoRefresh();
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = setInterval(() => {
      const slide = this.slides[this.activeSlide];
      if (slide?.type === 'map') {
        this.loadMapSlide(this.activeSlide, false);
      } else {
        this.fetchSlide(this.activeSlide, false, undefined, !this.userPickedDateTab);
      }
      this.preloadNextSlide(true);
    }, this.refreshMs);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  get activeReport(): HygieneStaffReport | null {
    return this.slides[this.activeSlide]?.report ?? null;
  }

  get activePictures(): any[] {
    return this.picturesOf(this.slides[this.activeSlide]);
  }

  picturesOf(slide: LiveSlide | null | undefined): any[] {
    return (slide?.report?.pictures as any[]) || [];
  }

  summaryCount(tab: DateTab): number {
    const report = this.activeReport;
    return report ? this.countForTab(report, tab) : 0;
  }

  isComboSlide(slide: LiveSlide): boolean {
    return slide.type === 'combo';
  }

  isMapSlide(slide: LiveSlide): boolean {
    return slide.type === 'map';
  }

  get activeSlideLabel(): string {
    return this.slides[this.activeSlide]?.label ?? '';
  }

  // ─── Auto rotation (carousel) ───────────────────────────────────────────────
  private nextSlideIndex(from: number = this.activeSlide): number {
    return (from + 1) % this.slides.length;
  }

  private startAutoRotate(): void {
    this.stopAutoRotate();
    this.autoRotateTimer = setInterval(() => this.autoAdvanceSlide(), this.autoRotateMs);
  }

  private stopAutoRotate(): void {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  /** Auto-rotate: show preloaded next slide (no loader); fetch next if not ready yet */
  private autoAdvanceSlide(): void {
    const nextIdx = this.nextSlideIndex();
    const showPreloaded = () => {
      this.applySlideIndex(nextIdx);
      this.preloadNextSlide();
    };

    if (this.isSlideReady(nextIdx)) {
      showPreloaded();
      return;
    }

    this.fetchSlide(nextIdx, false, () => showPreloaded(), true);
  }

  private isSlideReady(index: number): boolean {
    const slide = this.slides[index];
    if (!slide) {
      return false;
    }
    if (slide.type === 'map') {
      return !!slide.mapReady;
    }
    return slide.report != null;
  }

  private applySlideIndex(index: number): void {
    const len = this.slides.length;
    this.activeSlide = ((index % len) + len) % len;
    this.userPickedDateTab = false;
    this.imageErrorIds.clear();
    const slide = this.slides[this.activeSlide];
    if (slide?.type !== 'map') {
      const tab = slide?.resolvedDateTab;
      if (tab) {
        this.activeDateTab = tab;
      }
    }
  }

  private emptyReport(): HygieneStaffReport {
    return {
      todayPhotoCount: 0,
      yesterdayPhotoCount: 0,
      last7DaysPhotoCount: 0,
      thisMonthPhotoCount: 0,
      pictures: []
    };
  }

  private normalizeBuildingLocations(res: unknown): HygieneBuildingLocation[] {
    const rawList = Array.isArray(res)
      ? res
      : Array.isArray((res as { data?: unknown })?.data)
        ? ((res as { data: unknown[] }).data as HygieneBuildingLocation[])
        : [];

    return rawList.map((item) => {
      const record = item as Record<string, unknown>;
      return {
        ...(item as HygieneBuildingLocation),
        hygienePhotoTaken: this.resolveHygienePhotoTaken(record),
      };
    });
  }

  private resolveHygienePhotoTaken(loc: Record<string, unknown>): boolean {
    const raw = loc['hygienePhotoTaken'];
    if (typeof raw === 'boolean') {
      return raw;
    }
    if (raw === 1 || raw === '1') {
      return true;
    }
    if (raw === 0 || raw === '0' || raw == null || raw === '') {
      return false;
    }
    const normalized = String(raw).trim().toLowerCase();
    return normalized === 'true' || normalized === 'yes' || normalized === 'y';
  }

  private buildHygieneMapMarkerOptions(
    photoTaken: boolean,
    title: string
  ): google.maps.MarkerOptions {
    const fill = photoTaken ? '#22c55e' : '#ef4444';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="${fill}" stroke="#ffffff" stroke-width="1"/></svg>`;
    return {
      title,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: { width: 10, height: 10 },
        anchor: { x: 5, y: 5 },
      } as google.maps.Icon,
    };
  }

  private applyBuildingLocationsToMap(locations: HygieneBuildingLocation[]): void {
    const markerByKey = new Map<string, HygieneMapMarker>();

    for (const loc of locations) {
      const lat = parseFloat(String(loc.latitude ?? ''));
      const lng = parseFloat(String(loc.longitude ?? ''));
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }
      const key = `${loc.buildingId ?? ''}-${lat.toFixed(6)},${lng.toFixed(6)}`;
      const photoTaken = !!loc.hygienePhotoTaken;
      const title = loc.buildingName || 'Building';
      const existing = markerByKey.get(key);
      if (existing) {
        if (photoTaken && existing.options.icon && typeof existing.options.icon === 'object') {
          existing.options = this.buildHygieneMapMarkerOptions(true, title);
        }
        continue;
      }
      markerByKey.set(key, {
        position: { lat, lng },
        title,
        options: this.buildHygieneMapMarkerOptions(photoTaken, title),
      });
    }

    const markers = Array.from(markerByKey.values());
    this.mapMarkers = markers;
    if (markers.length === 0) {
      return;
    }
    this.mapCenter = { ...markers[0].position };
    this.mapZoom = this.defaultMapZoom;
  }

  private loadMapSlide(slideIndex: number, showSpinner: boolean, onComplete?: () => void): void {
    const slide = this.slides[slideIndex];
    if (!slide || slide.type !== 'map') {
      onComplete?.();
      return;
    }
    if (showSpinner) {
      this.spinner.show();
    }

    const filterType = this.filterTypeForTab(this.activeDateTab);
    const comboSlides = this.slides.filter((s) => s.type === 'combo');
    const requests = comboSlides.map((s) =>
      this.hygineLiveService.getBuildingLocations({
        schooolOrCollege: s.college,
        boardingType: s.boarding,
        filterType
      })
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        const locations = results.flatMap((res) => this.normalizeBuildingLocations(res));
        this.applyBuildingLocationsToMap(locations);
        slide.report = slide.report ?? this.emptyReport();
        slide.mapReady = true;
        if (showSpinner) {
          this.spinner.hide();
        }
        onComplete?.();
      },
      error: () => {
        this.mapMarkers = [];
        slide.report = this.emptyReport();
        slide.mapReady = true;
        if (showSpinner) {
          this.spinner.hide();
        }
        onComplete?.();
      }
    });
  }

  /** Silently preload the upcoming auto-rotate slide */
  private preloadNextSlide(force = false): void {
    const nextIdx = this.nextSlideIndex();
    if (!force && this.isSlideReady(nextIdx)) {
      return;
    }
    if (this.preloadingSlideIndex === nextIdx) {
      return;
    }
    this.preloadingSlideIndex = nextIdx;
    this.fetchSlide(
      nextIdx,
      false,
      () => {
        if (this.preloadingSlideIndex === nextIdx) {
          this.preloadingSlideIndex = null;
        }
      },
      true
    );
  }

  goToSlide(index: number): void {
    const len = this.slides.length;
    const newIndex = ((index % len) + len) % len;
    if (newIndex === this.activeSlide) {
      return;
    }

    this.userPickedDateTab = false;
    this.imageErrorIds.clear();

    const showSlide = () => {
      this.applySlideIndex(newIndex);
      this.preloadNextSlide(true);
    };

    if (this.isSlideReady(newIndex)) {
      showSlide();
      return;
    }

    this.fetchSlide(newIndex, false, () => showSlide(), true);
  }

  nextSlide(): void {
    this.navigateByArrow(this.activeSlide + 1);
    this.startAutoRotate();
  }

  prevSlide(): void {
    this.navigateByArrow(this.activeSlide - 1);
    this.startAutoRotate();
  }

  /** Arrow navigation: always fetch with loader, then preload next slide */
  private navigateByArrow(index: number): void {
    const len = this.slides.length;
    const newIndex = ((index % len) + len) % len;
    const isMap = this.slides[newIndex]?.type === 'map';
    this.userPickedDateTab = false;
    this.imageErrorIds.clear();
    this.fetchSlide(newIndex, !isMap, () => {
      this.applySlideIndex(newIndex);
      this.preloadNextSlide(true);
    }, !isMap);
  }

  pauseCarousel(): void {
    this.stopAutoRotate();
  }

  resumeCarousel(): void {
    this.startAutoRotate();
  }

  // ─── Date filter (applies to every slide) ────────────────────────────────────
  selectDateTab(tab: DateTab): void {
    this.userPickedDateTab = true;
    this.activeDateTab = tab;
    this.slides.forEach((slide, index) => {
      if (index !== this.activeSlide) {
        slide.report = null;
        slide.resolvedDateTab = undefined;
        if (slide.type === 'map') {
          slide.mapReady = false;
        }
      }
    });
    this.imageErrorIds.clear();
    this.spinner.show();
    this.fetchSlide(this.activeSlide, true, () => this.preloadNextSlide(true), false);
  }

  private filterTypeForTab(tab: DateTab): string {
    return this.dateTabPriority.find((d) => d.tab === tab)?.filterType ?? 'TODAY';
  }

  private dateFilterType(): string {
    return this.filterTypeForTab(this.activeDateTab);
  }

  private filterTypeForSlide(slideIndex: number): string {
    const slide = this.slides[slideIndex];
    if (!slide) {
      return 'TODAY';
    }
    if (slideIndex === this.activeSlide) {
      return this.filterTypeForTab(this.activeDateTab);
    }
    if (slide.resolvedDateTab) {
      return this.filterTypeForTab(slide.resolvedDateTab);
    }
    return this.filterTypeForTab(this.activeDateTab);
  }

  private countForTab(report: HygieneStaffReport, tab: DateTab): number {
    switch (tab) {
      case 'today':
        return report.todayPhotoCount ?? 0;
      case 'yesterday':
        return report.yesterdayPhotoCount ?? 0;
      case 'last7':
        return report.last7DaysPhotoCount ?? 0;
      case 'month':
        return report.thisMonthPhotoCount ?? 0;
    }
  }

  /** Pick first date range (left to right) with count > 0 */
  private resolveDateTabFromReport(report: HygieneStaffReport): DateTab {
    for (const { tab } of this.dateTabPriority) {
      if (this.countForTab(report, tab) > 0) {
        return tab;
      }
    }
    return 'today';
  }

  isDateTabActive(tab: DateTab, slideIndex: number): boolean {
    if (this.slides[slideIndex]?.type === 'map') {
      return false;
    }
    if (slideIndex === this.activeSlide) {
      return this.activeDateTab === tab;
    }
    return (this.slides[slideIndex]?.resolvedDateTab ?? this.activeDateTab) === tab;
  }

  private fetchAllSlides(showSpinner: boolean = true, applyFallback: boolean = true): void {
    if (showSpinner) {
      this.imageErrorIds.clear();
      this.spinner.show();
    }
    let pending = this.slides.length;
    const done = () => {
      pending -= 1;
      if (pending <= 0 && showSpinner) {
        this.spinner.hide();
      }
    };
    this.slides.forEach((_, index) => this.fetchSlide(index, false, done, applyFallback));
  }

  /** One API call per slide with that slide's college + boarding + date filter */
  private fetchSlide(
    slideIndex: number,
    showSpinner: boolean = false,
    onComplete?: () => void,
    applyFallback: boolean = false
  ): void {
    const slide = this.slides[slideIndex];
    if (!slide) {
      onComplete?.();
      return;
    }
    if (slide.type === 'map') {
      this.loadMapSlide(slideIndex, showSpinner, onComplete);
      return;
    }
    if (applyFallback) {
      this.fetchSlideWithDateFallback(slideIndex, showSpinner, onComplete);
      return;
    }
    if (showSpinner) {
      this.spinner.show();
    }
    this.requestSlideReport(slide, this.filterTypeForSlide(slideIndex)).subscribe({
      next: (res: any) => {
        slide.report = this.mapReportResponse(res);
        this.onSlideReportLoaded(slideIndex, this.activeDateTab, showSpinner, onComplete);
      },
      error: () => {
        slide.report = null;
        if (showSpinner) {
          this.spinner.hide();
        }
        onComplete?.();
      }
    });
  }

  /** Fetch TODAY counts first, then load pictures from the first range with count > 0 */
  private fetchSlideWithDateFallback(slideIndex: number, showSpinner: boolean, onComplete?: () => void): void {
    const slide = this.slides[slideIndex];
    if (!slide || slide.type !== 'combo') {
      onComplete?.();
      return;
    }
    if (showSpinner) {
      this.spinner.show();
    }

    this.requestSlideReport(slide, 'TODAY').subscribe({
      next: (res: any) => {
        const baseReport = this.mapReportResponse(res);
        const tab = this.resolveDateTabFromReport(baseReport);
        slide.resolvedDateTab = tab;
        const filterType = this.filterTypeForTab(tab);

        if (filterType === 'TODAY') {
          slide.report = baseReport;
          this.onSlideReportLoaded(slideIndex, tab, showSpinner, onComplete);
          return;
        }

        this.requestSlideReport(slide, filterType).subscribe({
          next: (detailRes: any) => {
            const detailReport = this.mapReportResponse(detailRes);
            slide.report = {
              ...baseReport,
              pictures: detailReport.pictures,
              totalCount: detailReport.totalCount,
              totalPages: detailReport.totalPages
            };
            this.onSlideReportLoaded(slideIndex, tab, showSpinner, onComplete);
          },
          error: () => {
            slide.report = baseReport;
            this.onSlideReportLoaded(slideIndex, tab, showSpinner, onComplete);
          }
        });
      },
      error: () => {
        slide.report = null;
        if (showSpinner) {
          this.spinner.hide();
        }
        onComplete?.();
      }
    });
  }

  private requestSlideReport(slide: LiveSlide, filterType: string) {
    const params = {
      schooolOrCollege: slide.college!,
      boardingType: slide.boarding!,
      filterType,
      page: 1,
      pageSize: this.pageSize
    };
    return this.hygineLiveService.getStaffReportByPagenation(params);
  }

  private onSlideReportLoaded(
    slideIndex: number,
    tab: DateTab,
    showSpinner: boolean,
    onComplete?: () => void
  ): void {
    const mapSlide = this.slides.find((s) => s.type === 'map');
    if (mapSlide) {
      mapSlide.mapReady = false;
      mapSlide.report = null;
    }
    if (slideIndex === this.activeSlide && !this.userPickedDateTab && this.slides[slideIndex]?.type === 'combo') {
      this.activeDateTab = tab;
    }
    if (showSpinner) {
      this.spinner.hide();
    }
    onComplete?.();
  }
  mapReportResponse(res: any): HygieneStaffReport {
    const data = res?.data ?? res;
    const pictures = Array.isArray(data?.pictures) ? data.pictures : (Array.isArray(data?.images) ? data.images : []);
    return {
      todayPhotoCount: data?.todayPhotoCount ?? 0,
      yesterdayPhotoCount: data?.yesterdayPhotoCount ?? 0,
      last7DaysPhotoCount: data?.last7DaysPhotoCount ?? 0,
      thisMonthPhotoCount: data?.thisMonthPhotoCount ?? 0,
      pictures,
      totalCount: data?.totalCount ?? res?.totalCount,
      totalPages: data?.totalPages ?? res?.totalPages
    };
  }

  onImageError(imageRefId: number): void {
    this.imageErrorIds.add(imageRefId);
  }

  openImageDialog(p: any): void {
    this.dialog.open(HyginestaftImageDialogComponent, {
      data: { picture: p },
      width: 'min(1000px, 95vw)',
      maxWidth: '1000px',
      minWidth: '320px',
      panelClass: 'hygiene-image-dialog'
    });
  }

  openMap(lat: string | number, lng: string | number): void {
    if (lat == null || lng == null) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(lat))},${encodeURIComponent(String(lng))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onMapIconClick(event: Event, lat: string | number | null | undefined, lng: string | number | null | undefined): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (lat != null && lng != null && lat !== '' && lng !== '') {
      this.openMap(lat, lng);
    } else {
      window.open('https://www.google.com/maps', '_blank', 'noopener,noreferrer');
    }
  }

  getPictureDateTime(p: any): string {
    const raw = (p?.hygieneDate ?? p?.capturedTime ?? p?.uploadedTime ?? '') as string;
    return this.formatHygieneDateTimeForCard(raw);
  }

  formatHygieneDateTimeForCard(dateStr: string): string {
    if (!dateStr) return '';
    const s = String(dateStr).trim();

    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
      const dt = new Date(s);
      if (!Number.isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const mo = dt.getMonth() + 1;
        const d = dt.getDate();
        const hh = dt.getHours();
        const mi = dt.getMinutes();
        const sec = dt.getSeconds();
        const dd = String(d).padStart(2, '0');
        const mm = String(mo).padStart(2, '0');
        const dateLabel = `${dd}-${mm}-${y}`;
        const isMidnight = hh === 0 && mi === 0 && sec === 0;
        if (isMidnight) {
          return dateLabel;
        }
        const ampm = hh >= 12 ? 'PM' : 'AM';
        const h12 = hh % 12 || 12;
        const timeLabel = `${h12}:${String(mi).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${ampm}`;
        return `${dateLabel} · ${timeLabel}`;
      }
    }

    let y = 0;
    let mo = 0;
    let d = 0;
    let hh = 0;
    let mi = 0;
    let sec = 0;
    let parsed = false;

    if (!parsed && /^\d{2}-\d{2}-\d{4}/.test(s)) {
      const [datePart, timePart] = s.split(/\s+/);
      const [ds, ms, ys] = datePart.split('-').map((x) => parseInt(x, 10));
      if (![ds, ms, ys].some((n) => Number.isNaN(n))) {
        d = ds;
        mo = ms;
        y = ys;
        if (timePart) {
          const tp = timePart.split(/[:.]/).slice(0, 3).map((x) => parseInt(x, 10));
          hh = tp[0] || 0;
          mi = tp[1] || 0;
          sec = tp[2] || 0;
        }
        parsed = true;
      }
    }

    if (!parsed) {
      return s;
    }

    const dd = String(d).padStart(2, '0');
    const mm = String(mo).padStart(2, '0');
    const dateLabel = `${dd}-${mm}-${y}`;
    const isMidnight = hh === 0 && mi === 0 && sec === 0;
    if (isMidnight) {
      return dateLabel;
    }
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    const timeLabel = `${h12}:${String(mi).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${ampm}`;
    return `${dateLabel} · ${timeLabel}`;
  }
}
